import {
  getCompetitionLevelsByCompetitionLevelStats,
  getPlayersByPlayerIdStats,
} from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";

import { PlayerType } from "@/lib/types";

type UsePlayerStatsParam = {
  playerId: string;
  startDate: string | null;
  endDate: string | null;
  playerType: PlayerType;
};

export const usePlayerStats = ({
  playerId,
  startDate,
  endDate,
  playerType,
}: UsePlayerStatsParam) => {
  const { isLoading, data, error } = useQuery({
    queryFn: async () => {
      const [gameStats, practiceStats, d1GameStats, d1PracticeStats] =
        await Promise.all([
          getPlayersByPlayerIdStats({
            path: { player_id: playerId },
            query: {
              startDate: startDate!,
              endDate: endDate!,
              gameType: "game",
              playerType: playerType,
            },
            throwOnError: true,
          }).catch((error) => {
            if (error.message.includes("hasn't played")) {
              return { data: null, noDataAvailable: true };
            }
            throw error;
          }),
          getPlayersByPlayerIdStats({
            path: { player_id: playerId },
            query: {
              startDate: startDate!,
              endDate: endDate!,
              gameType:
                playerType === "pitcher" ? "bullpen" : "batting_practice",
              playerType: playerType,
            },
            throwOnError: true,
          }).catch((error) => {
            if (error.message.includes("hasn't played")) {
              return { data: null, noDataAvailable: true };
            }
            throw error;
          }),
          getCompetitionLevelsByCompetitionLevelStats({
            path: { competition_level: "ncaa_d1" },
            query: {
              startDate: startDate!,
              endDate: endDate!,
              gameType: "game",
              playerType: playerType,
            },
            throwOnError: true,
          }).catch((error) => {
            if (
              error.message.includes("No competition level data available.")
            ) {
              return { data: null, noDataAvailable: true };
            }
            throw error;
          }),
          getCompetitionLevelsByCompetitionLevelStats({
            path: { competition_level: "ncaa_d1" },
            query: {
              startDate: startDate!,
              endDate: endDate!,
              gameType:
                playerType === "pitcher" ? "bullpen" : "batting_practice",
              playerType: playerType,
            },
            throwOnError: true,
          }).catch((error) => {
            if (
              error.message.includes("No competition level data available.")
            ) {
              return { data: null, noDataAvailable: true };
            }
            throw error;
          }),
        ]);
      return {
        gameStats: gameStats.data,
        practiceStats: practiceStats.data,
        d1GameStats: d1GameStats.data,
        d1PracticeStats: d1PracticeStats.data,
      };
    },
    queryKey: ["player-stats", playerId, playerType, startDate, endDate],
    enabled: !!playerId && !!startDate && !!endDate,
    retry: false,
  });

  const { gameStats, practiceStats, d1GameStats, d1PracticeStats } = data ?? {};

  return {
    isLoading,
    data:
      gameStats || practiceStats || d1GameStats || d1PracticeStats
        ? { gameStats, practiceStats, d1GameStats, d1PracticeStats }
        : null,
    error,
  };
};
